import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";

import { useTranslation } from "react-i18next"; // Added import for translations
import SearchCar from "../addCar/SearchCar";

import AddEzPassTagNumber from "../addCar/AddEzPassTagNumber";
import AddImages from "../addCar/AddImages";
import AddPlateNumber from "../addCar/AddPlateNumber";
import AddVIN from "../addCar/AddVIN";
import ModalLayout from "../addCar/ModalLayout";
import PreviewCarDetails from "../addCar/PreviewCarDetails";
import SelectCarModel from "../addCar/SelectCarModel";
import SelectCategory from "../addCar/SelectCategory";
import SelectColor from "../addCar/SelectColor";
import SelectLocation from "../addCar/SelectLocation";
import SelectYear from "../addCar/SelectYear";
import apiClient from "../../../../helpers/apiClient";
// import ImageCompressor from "../../../../utilities/ImageCompressor";
import AddCarCompany from "../addCar/AddCarCompany";
import AddCarModel from "../addCar/AddCarModel";
import AddYear from "../addCar/AddYear";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import uploadFile from "../../../../utilities/uploadFile";
import AddRegistrationPDF from "../addCar/AddRegistrationPDF";
import AddWorthValue from "../addCar/AddWorthValue";
import SetCarCapacity from "../addCar/SetCarCapacity";
import SetCarPrice from "../addCar/SetCarPrice";
import useCarPricingMode from "../../hooks/useCarPricingMode";
import { useToast } from "../../../../contexts/ToastContext";
import { buildRatePayload, createDefaultRate, isRateStepValid } from "../addCar/RateFields";
import VinVerifyStep from "../addCar/VinVerifyStep";

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

const getWizardSteps = (isCarPricingMode) => ({
    CATEGORY: 5,
    SET_CAPACITY: isCarPricingMode ? 6 : -1,
    SET_PRICE: isCarPricingMode ? 7 : -1,
    LOCATION: isCarPricingMode ? 8 : 6,
    PLATE: isCarPricingMode ? 9 : 7,
    WORTH: isCarPricingMode ? 10 : 8,
    EZPASS: isCarPricingMode ? 11 : 9,
    IMAGES: isCarPricingMode ? 12 : 10,
    REGISTRATION: isCarPricingMode ? 13 : 11,
    PREVIEW: isCarPricingMode ? 14 : 12,
});

const AddCarModal = ({
    open = true,
    onClose = () => {},
    refetchCars,
    decodedVehiclePrefill,
    entryFlow = "manual",
    manualVinPrefill = "",
}) => {
    const { t } = useTranslation("cars"); // Added translation hook
    const toast = useToast();

    // data states
    const [selectedCompany, setSelectedCompany] = useState("");
    const [, setCarModels] = useState([]);
    const [selectedCarModel, setSelectedCarModel] = useState({
        make: "",
        model: "",
        vclass: "",
        fueltype1: "",
        trany: "",
    });
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedColor, setSelectedColor] = useState("");
    const [selectedLocation, setSelectedLocation] = useState({});
    const [plateNumber, setPlateNumber] = useState("");
    const [selectedCategory, setSelectedCategory] = useState({});
    const [seats, setSeats] = useState();
    const [bags, setBags] = useState();
    const [ezPassTagNumber, setEzPassTagNumber] = useState("");
    const [images, setImages] = useState([]);
    const [vin, setVin] = useState("");
    const [registrationPDF, setRegistrationPDF] = useState(null);
    const [worthValue, setWorthValue] = useState(null);
    const [carRate, setCarRate] = useState(createDefaultRate);

    // validation states
    const [isValidVIN, setIsValidVIN] = useState(true);
    const [isDuplicateVIN, setIsDuplicateVIN] = useState(false);
    const [isDuplicatePlateNumber, setIsDuplicatePlateNumber] = useState(false);
    const [isDuplicateEzPassTagNumber, setIsDuplicateEzPassTagNumber] = useState(false);

    // progress state
    const [step, setStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const [loading, setLoading] = useState(false);
    const { myCompanyData, fetchCompanyData } = useUser();
    const [isInsuranceEnabled, setIsInsuranceEnabled] = useState(true);
    const isCarPricingMode = useCarPricingMode();
    const wizardSteps = useMemo(() => getWizardSteps(isCarPricingMode), [isCarPricingMode]);

    // conditional states
    const [isManualCarAddMode, setIsManualCarAddMode] = useState(false);
    const [vinEntryActive, setVinEntryActive] = useState(false);

    const applyDecodedVehiclePrefill = useCallback((p) => {
        const vinClean = (p.vin || "").trim().toUpperCase();
        setIsManualCarAddMode(true);
        setVin(vinClean);
        setIsValidVIN(true);
        setSelectedCompany(p.make || "");
        setSelectedCarModel({
            make: p.make || "",
            model: p.model || "",
            vclass: p.vclass || "",
            fueltype1: p.fueltype1 || "",
            trany: p.trany || "",
        });
        setSelectedYear(Number.isFinite(p.year) ? p.year : null);
        const manualSteps = 12;
        const targetStep = 3;
        setStep(targetStep);
        setProgress((targetStep / manualSteps) * 100);
        setVinEntryActive(false);
    }, []);

    // Total steps - depends on manual mode and pricing mode
    const totalProgressSteps = (isManualCarAddMode ? 12 : 10) + (isCarPricingMode ? 2 : 0);

    useEffect(() => {
        const insuranceCheck = () => {
            if (!myCompanyData?.insurance) {
                setIsInsuranceEnabled(false);
                return;
            }

            const teqmobilityInsurance = myCompanyData.insurance.find(
                (insurance) => insurance.provider === "teqmobility",
            );

            setIsInsuranceEnabled(teqmobilityInsurance?.enabled === true);
        };

        // Only check if we have company data
        if (myCompanyData) {
            insuranceCheck();
        }
    }, [myCompanyData]);

    // Also add a separate useEffect to fetch company data on mount
    useEffect(() => {
        fetchCompanyData();
    }, [fetchCompanyData]);

    const handleNextStep = () => {
        setStep((prevStep) => {
            const nextStep = prevStep + 1;
            // Update progress based on mode
            if (isManualCarAddMode) {
                // In manual mode, start progress from step 0
                if (prevStep >= 0) {
                    setProgress((prevProgress) => prevProgress + 100 / totalProgressSteps);
                }
            } else if (prevStep >= 1) {
                // In search mode, start progress from step 1 (after search)
                setProgress((prevProgress) => prevProgress + 100 / totalProgressSteps);
            }
            return nextStep;
        });
    };

    const handleBackStep = () => {
        setStep((prevStep) => {
            const nextStep = prevStep - 1;
            // Update progress based on mode
            if (isManualCarAddMode) {
                // In manual mode, decrease progress from any step > 0
                if (prevStep > 0) {
                    setProgress((prevProgress) => prevProgress - 100 / totalProgressSteps);
                } else if (prevStep === 0) {
                    setProgress(0);
                }
            } else if (prevStep > 2) {
                setProgress((prevProgress) => prevProgress - 100 / totalProgressSteps);
            } else if (prevStep === 2) {
                setProgress(0);
            }

            return nextStep;
        });
    };

    const handleReset = () => {
        setStep(0);
        setProgress(0);
        setSelectedCompany("");
        setSelectedCarModel({});
        setSelectedYear(null);
        setSelectedColor("");
        setSelectedLocation({});
        setSelectedCategory({});
        setCarModels([]);
        setPlateNumber("");
        setSeats();
        setBags();
        setImages([]);
        setVin("");
        setEzPassTagNumber("");
        setIsDuplicateVIN(false);
        setIsDuplicatePlateNumber(false);
        setIsDuplicateEzPassTagNumber(false);
        setLoading(false);
        setIsManualCarAddMode(entryFlow === "manual");
        setVinEntryActive(false);
        setRegistrationPDF("");
        setWorthValue(null);
        setCarRate(createDefaultRate());
    };

    useLayoutEffect(() => {
        if (!open) {
            return undefined;
        }

        if (decodedVehiclePrefill) {
            applyDecodedVehiclePrefill(decodedVehiclePrefill);
            return undefined;
        }

        if (entryFlow === "vin-first") {
            setVinEntryActive(true);
            setIsManualCarAddMode(false);
            setStep(0);
            setProgress(0);
        } else if (entryFlow === "manual") {
            setVinEntryActive(false);
            setIsManualCarAddMode(true);
            setStep(0);
            setProgress(0);
            if (manualVinPrefill) {
                setVin(manualVinPrefill);
                setIsValidVIN(VIN_PATTERN.test(manualVinPrefill));
            }
        }

        return undefined;
    }, [open, decodedVehiclePrefill, entryFlow, manualVinPrefill, applyDecodedVehiclePrefill]);

    const handleVinContinue = (decoded) => {
        applyDecodedVehiclePrefill(decoded);
    };

    const handleVinManualEntry = (vinValue) => {
        setIsManualCarAddMode(true);
        setStep(0);
        setProgress(0);
        setVinEntryActive(false);
        if (vinValue) {
            setVin(vinValue);
            setIsValidVIN(VIN_PATTERN.test(vinValue));
        }
    };

    const carDetails = useMemo(
        () => ({
            company: selectedCarModel.make,
            model: selectedCarModel.model,
            year: selectedYear,
            color: selectedColor,
            location: selectedLocation.location,
            plateNumber,
            categoryName: selectedCategory.categoryName,
            bodyType: selectedCategory.bodyType,
            seats,
            bags,
            images,
            vin,
            ezPassTagNumber,
            registrationPDF,
            worthValue,
            rate: carRate,
            calenderDay: false,
            showCarPricing: isCarPricingMode,
        }),
        [
            selectedCarModel,
            selectedYear,
            selectedColor,
            selectedLocation,
            plateNumber,
            selectedCategory,
            seats,
            bags,
            images,
            vin,
            ezPassTagNumber,
            registrationPDF,
            worthValue,
            carRate,
            isCarPricingMode,
        ],
    );

    const canGoToNextStep = useMemo(() => {
        switch (step) {
            case 0:
                return !!selectedCompany.trim() && isManualCarAddMode;
            case 1:
                return !!selectedCarModel.model;
            case 2:
                return !!selectedYear;
            case 3:
                return !!selectedColor;
            case 4:
                return !!vin && isValidVIN;
            case wizardSteps.CATEGORY:
                return !!selectedCategory.category;
            case wizardSteps.SET_CAPACITY:
                return typeof seats === "number" && seats > 0 && typeof bags === "number" && bags >= 0;
            case wizardSteps.SET_PRICE:
                return isRateStepValid(carRate);
            case wizardSteps.LOCATION:
                return !!selectedLocation.locationId;
            case wizardSteps.PLATE:
                return !!plateNumber;
            case wizardSteps.WORTH:
                return worthValue !== null && worthValue >= 0;
            case wizardSteps.EZPASS:
                return true;
            case wizardSteps.IMAGES:
                return !!images.length;
            case wizardSteps.REGISTRATION:
                return true;
            default:
                return false;
        }
    }, [
        selectedCarModel.model,
        selectedYear,
        selectedColor,
        selectedCategory.category,
        selectedLocation.locationId,
        plateNumber,
        step,
        images,
        vin,
        isValidVIN,
        selectedCompany,
        isManualCarAddMode,
        worthValue,
        wizardSteps,
        seats,
        bags,
        carRate,
    ]);

    // Update progress when step changes
    const updateProgressOnStep = useCallback(
        (currentStep) => {
            if (isManualCarAddMode) {
                // In manual mode, show progress from step 0
                if (currentStep === 0) {
                    setProgress(0);
                } else {
                    setProgress((currentStep / totalProgressSteps) * 100);
                }
            } else if (currentStep === 2) {
                // In search mode, only show progress from step 2 onwards
                setProgress(100 / totalProgressSteps);
            } else if (currentStep < 2) {
                setProgress(0);
            } else {
                setProgress(((currentStep - 1) / totalProgressSteps) * 100);
            }
        },
        [isManualCarAddMode, totalProgressSteps],
    );

    // Call updateProgressOnStep when step or mode changes
    useMemo(() => {
        updateProgressOnStep(step);
    }, [updateProgressOnStep, step]);

    const handleAddCar = async () => {
        try {
            setLoading(true);

            if (images.length) {
                const imageResponses = await Promise.all(images.map((image) => uploadFile(image.originFileObj)));

                const imageUrls = {};
                imageResponses.forEach((url, index) => {
                    imageUrls[`image${index + 1}`] = url;
                });

                const details = {
                    carName: `${selectedCarModel.make} ${selectedCarModel.model} ${selectedYear}`,
                    carNumber: plateNumber.trim(),
                    categoryName: selectedCategory.categoryName,
                    category: selectedCategory.category,
                    bodyType: selectedCategory.categoryName,
                    location: selectedLocation.locationId,
                    serialNumber: "0000000",
                    VIN: vin.trim(),
                    config: {
                        make: selectedCarModel.make,
                        model: selectedCarModel.model,
                        year: Number(selectedYear),
                        color: selectedColor,
                        bags,
                        seats,
                        carSize: selectedCarModel.vclass,
                        fuelType: selectedCarModel.fueltype1,
                        transmission: selectedCarModel.trany,
                    },
                    images: imageUrls,
                    maintanenceMode: {
                        status: false,
                        startTime: "01/01/1900",
                    },
                    status: "Locked",
                    coverageHistory: [],
                    activeCoverage: null,
                    insuranceVehicleInfo: {
                        active: false,
                    },
                };

                if (worthValue !== null && worthValue >= 0) {
                    details.worthValue = worthValue;
                }

                if (ezPassTagNumber.trim()) {
                    details.ezPassTagNumber = ezPassTagNumber;
                }

                if (registrationPDF) {
                    const registrationPDFURL = await uploadFile(registrationPDF);
                    details.registration = { doc: registrationPDFURL };
                }

                if (isCarPricingMode) {
                    details.rate = buildRatePayload(carRate);
                    details.calenderDay = false;
                }

                await apiClient.post("api/car/create", details);

                toast.success({ message: t("carAddedSuccess") });
                handleReset();
                onClose();
                refetchCars();
            }
        } catch (error) {
            if (error.response?.data?.message === "Mongoose Duplicate Error") {
                if (error.response?.data?.errorSources[0]?.path === "VIN") {
                    setIsDuplicateVIN(true);
                    setStep(4);
                    // In manual mode, step 4 corresponds to progress step 4, in search mode it's step 3
                    const progressStep = isManualCarAddMode ? 4 : 3;
                    setProgress((100 / totalProgressSteps) * progressStep);
                }
                if (error.response?.data?.errorSources[0]?.path === "carNumber") {
                    setIsDuplicatePlateNumber(true);
                    setStep(wizardSteps.PLATE);
                    const progressStep = isManualCarAddMode ? wizardSteps.PLATE : wizardSteps.PLATE - 1;
                    setProgress((100 / totalProgressSteps) * progressStep);
                }
                if (error.response?.data?.errorSources[0]?.path === "ezPassTagNumber") {
                    setIsDuplicateEzPassTagNumber(true);
                    setStep(wizardSteps.EZPASS);
                    const progressStep = isManualCarAddMode ? wizardSteps.EZPASS : wizardSteps.EZPASS - 1;
                    setProgress((100 / totalProgressSteps) * progressStep);
                }
            } else {
                toast.error({
                    message: error.response?.data?.message || error.message || t("failedToAddCar"),
                });
            }

            // console.error("Error adding car:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={() => {
                onClose();
                handleReset();
            }}
            footer={null}
            closable={true}
            maskClosable={false}
            width={1000}
            className="!p-0 !min-h-[80vh]"
            title={t("addCar")} // Translated modal title
            styles={{
                header: {
                    textAlign: "center",
                },
            }}>
            {vinEntryActive ? (
                <VinVerifyStep onContinue={handleVinContinue} onManualEntry={handleVinManualEntry} />
            ) : null}

            {isManualCarAddMode || entryFlow === "vin-first" ? null : (
                <>
                    {step === 0 && (
                        <SearchCar
                            selectedCompany={selectedCompany}
                            setSelectedCompany={setSelectedCompany}
                            setStep={setStep}
                            t={t} // Pass translation function
                            setIsManualCarAddMode={setIsManualCarAddMode}
                        />
                    )}

                    {step === 1 && (
                        <SelectCarModel
                            selectedCarModel={selectedCarModel}
                            selectedCompany={selectedCompany}
                            setSelectedCarModel={setSelectedCarModel}
                            setStep={setStep}
                            t={t}
                        />
                    )}
                </>
            )}

            {!vinEntryActive && step !== wizardSteps.PREVIEW && (
                <ModalLayout
                    canGoToNextStep={canGoToNextStep}
                    selectedCarModel={selectedCarModel}
                    handleNextStep={handleNextStep}
                    handleBackStep={handleBackStep}
                    progress={progress}
                    t={t} // Pass translation function
                    step={step}
                    isManualCarAddMode={isManualCarAddMode}>
                    {isManualCarAddMode ? (
                        <>
                            {step === 0 && (
                                <AddCarCompany
                                    selectedCompany={selectedCompany}
                                    setSelectedCompany={setSelectedCompany}
                                    t={t}
                                    setIsManualCarAddMode={setIsManualCarAddMode}
                                    setSelectedCarModel={setSelectedCarModel}
                                    hideAutomateButton={entryFlow === "vin-first"}
                                />
                            )}

                            {step === 1 && (
                                <AddCarModel
                                    selectedCarModel={selectedCarModel}
                                    setSelectedCarModel={setSelectedCarModel}
                                    t={t}
                                />
                            )}
                        </>
                    ) : null}

                    {step === 2 ? (
                        isManualCarAddMode ? (
                            <AddYear
                                t={t}
                                selectedYear={selectedYear}
                                setSelectedYear={setSelectedYear}
                                setSelectedCarModel={setSelectedCarModel}
                            />
                        ) : (
                            <SelectYear
                                selectedCarModel={selectedCarModel}
                                selectedYear={selectedYear}
                                setSelectedYear={setSelectedYear}
                                setSelectedCarModel={setSelectedCarModel}
                                t={t}
                            />
                        )
                    ) : null}
                    {step === 3 && (
                        <SelectColor
                            selectedColor={selectedColor}
                            setSelectedColor={setSelectedColor}
                            t={t} // Pass translation function
                        />
                    )}
                    {step === 4 && (
                        <AddVIN
                            vin={vin}
                            setVin={setVin}
                            t={t}
                            isDuplicateVIN={isDuplicateVIN}
                            setIsDuplicateVIN={setIsDuplicateVIN}
                            setIsValidVIN={setIsValidVIN}
                        />
                    )}
                    {step === wizardSteps.CATEGORY && (
                        <SelectCategory
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            setBags={setBags}
                            setSeats={setSeats}
                            t={t}
                        />
                    )}
                    {isCarPricingMode && step === wizardSteps.SET_CAPACITY && (
                        <SetCarCapacity
                            seats={seats}
                            setSeats={setSeats}
                            bags={bags}
                            setBags={setBags}
                            t={t}
                        />
                    )}
                    {isCarPricingMode && step === wizardSteps.SET_PRICE && (
                        <SetCarPrice rate={carRate} setRate={setCarRate} t={t} />
                    )}
                    {step === wizardSteps.LOCATION && (
                        <SelectLocation
                            selectedLocation={selectedLocation}
                            setSelectedLocation={setSelectedLocation}
                            t={t}
                            isCompanyInsuranceEnabled={isInsuranceEnabled}
                        />
                    )}
                    {step === wizardSteps.PLATE && (
                        <AddPlateNumber
                            plateNumber={plateNumber}
                            setPlateNumber={setPlateNumber}
                            t={t}
                            isDuplicatePlateNumber={isDuplicatePlateNumber}
                            setIsDuplicatePlateNumber={setIsDuplicatePlateNumber}
                        />
                    )}
                    {step === wizardSteps.WORTH && (
                        <AddWorthValue worthValue={worthValue} setWorthValue={setWorthValue} t={t} />
                    )}
                    {step === wizardSteps.EZPASS && (
                        <AddEzPassTagNumber
                            ezPassTagNumber={ezPassTagNumber}
                            setEzPassTagNumber={setEzPassTagNumber}
                            t={t}
                            isDuplicateEzPassTagNumber={isDuplicateEzPassTagNumber}
                            setIsDuplicateEzPassTagNumber={setIsDuplicateEzPassTagNumber}
                        />
                    )}
                    {step === wizardSteps.IMAGES && (
                        <AddImages images={images} setImages={setImages} t={t} />
                    )}
                    {step === wizardSteps.REGISTRATION && (
                        <AddRegistrationPDF
                            setRegistrationPDF={setRegistrationPDF}
                            selectedFile={registrationPDF}
                            t={t}
                        />
                    )}
                </ModalLayout>
            )}
            {!vinEntryActive && step === wizardSteps.PREVIEW && (
                <PreviewCarDetails
                    handleBackStep={handleBackStep}
                    carDetails={carDetails}
                    handleAddCar={handleAddCar}
                    loading={loading}
                    t={t} // Pass translation function
                />
            )}
        </Modal>
    );
};

AddCarModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    refetchCars: PropTypes.func.isRequired,
    decodedVehiclePrefill: PropTypes.shape({
        vin: PropTypes.string.isRequired,
        make: PropTypes.string,
        model: PropTypes.string,
        year: PropTypes.number,
        fueltype1: PropTypes.string,
        trany: PropTypes.string,
        vclass: PropTypes.string,
    }),
    entryFlow: PropTypes.oneOf(["vin-first", "manual"]),
    manualVinPrefill: PropTypes.string,
};

AddCarModal.defaultProps = {
    decodedVehiclePrefill: null,
    entryFlow: "manual",
    manualVinPrefill: "",
};

export default AddCarModal;
